import React from 'react'
import { useMsal, useAccount } from '@azure/msal-react'
import { apiRequest } from './apiClient'

export interface ReportSummary {
  total: number
  passed: number
  failed: number
  skipped: number
  flaky: number
}

export interface EnvSummary {
  reportName: string | null
  lastUpdated: string | null
  summary: ReportSummary | null
  passRate: number | null
  hasPlaywright: boolean
}

export interface ProductSummary {
  key: string
  label: string
  dev: EnvSummary
  prod: EnvSummary
}

export type CheckStatus = 'up' | 'down' | 'unknown'

export interface HealthResult {
  status: CheckStatus
  statusCode?: number
  latencyMs?: number
  checkedAt: string
  error?: string
}

export interface ProductHealth {
  key: string
  label: string
  fe: { dev: HealthResult; prod: HealthResult }
  be: { dev: HealthResult; prod: HealthResult }
}

export interface CiStatus {
  key: string
  status: string
  conclusion: string | null
  runUrl: string | null
  updatedAt: string | null
}

export interface Ec2Status {
  status: CheckStatus
  state?: string
  statusChecksOk?: boolean
  name?: string
  instanceId: string
  checkedAt: string
  error?: string
}

export interface GithubRunner {
  name: string
  status: CheckStatus
  busy: boolean
  activeRun?: { workflowName: string; runUrl: string; startedAt: string }
}

export interface InfraStatus {
  ec2: Ec2Status
  githubRunners: { runners: GithubRunner[]; error?: string }
}

const POLL_INTERVAL_MS = 60 * 1000

// Shared by the Status Dashboard (core apps + infra) and the Additional Services overview
export function useDashboardData(tier: 'core' | 'additional', withInfra: boolean) {
  const { instance } = useMsal()
  const account = useAccount()

  const [summaries, setSummaries] = React.useState<ProductSummary[]>([])
  const [health, setHealth] = React.useState<ProductHealth[]>([])
  const [ciStatus, setCiStatus] = React.useState<CiStatus[]>([])
  const [infraStatus, setInfraStatus] = React.useState<InfraStatus | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [isSyncing, setIsSyncing] = React.useState(false)
  const [lastSync, setLastSync] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const fetchAll = React.useCallback(async () => {
    if (!account) return
    const get = <T>(path: string) => apiRequest<T>(instance, account, 'get', path)

    try {
      const [summaryData, healthData, ciData, infraData] = await Promise.all([
        get<{ products: ProductSummary[] }>(`/api/dashboard/summary?tier=${tier}`),
        get<{ products: ProductHealth[] }>('/api/health-checks'),
        get<{ products: CiStatus[] }>('/api/ci-status').catch(() => ({ products: [] })),
        withInfra ? get<InfraStatus>('/api/infra-status').catch(() => null) : Promise.resolve(null),
      ])

      setSummaries(summaryData.products ?? [])
      setHealth(healthData.products ?? [])
      setCiStatus(ciData.products ?? [])
      setInfraStatus(infraData)
      setLastSync(new Date().toISOString())
      setError(null)
    } catch (err) {
      console.error('Error fetching dashboard data:', err)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }, [account, instance, tier, withInfra])

  React.useEffect(() => {
    fetchAll()
    const interval = setInterval(fetchAll, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchAll])

  // Re-runs the server-side checks (they otherwise refresh every 15-30 min) before refetching
  const refresh = async () => {
    setIsSyncing(true)
    if (account) {
      const post = (path: string) => apiRequest(instance, account, 'post', path, 20000)
      try {
        await Promise.all([
          post('/api/health-checks/refresh'),
          ...(withInfra ? [post('/api/infra-status/refresh')] : []),
        ])
      } catch (err) {
        console.error('Error re-running status checks:', err)
      }
    }
    await fetchAll()
    setIsSyncing(false)
  }

  return { summaries, health, ciStatus, infraStatus, loading, isSyncing, lastSync, error, refresh }
}
